import React, { useEffect, useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";

import lunaPng from "../assets/images/services-hero-luna.png";
import liquidPng from "../assets/images/services-hero-liquid-iv.png";
import bacardiPng from "../assets/images/services-hero-bacardi.png";

const opacity = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const EXPAND_DISTANCE = 250;

const appearParent = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.15 }
  }
};

const appearChild = {
  hidden: { opacity: 0, y: 12, scale: 0.98 },
  show: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.45, ease: "easeOut" }
  }
};

export default function ServicesHero({
  moveBacardiX = "-100%",
  moveBacardiY = "130%",
  moveLiquidX = "-200%",
  moveLiquidY = "-50%",
  moveLunaX = "120%",
  moveLunaY = "100%",
}) {
  const sectionRef = useRef(null);
  const t = useMotionValue(0);

  useEffect(() => {
    const getScrollY = () =>
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const onScroll = () => {
      const y = Math.max(0, getScrollY());
      const p = Math.min(1, y / EXPAND_DISTANCE);
      t.set(p);
    };

    t.set(0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [t]);

  const bacardiX = useTransform(t, [0, 1], ["0%", moveBacardiX]);
  const bacardiY = useTransform(t, [0, 1], ["0%", moveBacardiY]);

  const liquidX = useTransform(t, [0, 1], ["0%", moveLiquidX]);
  const liquidY = useTransform(t, [0, 1], ["0%", moveLiquidY]);

  const lunaX = useTransform(t, [0, 1], ["0%", moveLunaX]);
  const lunaY = useTransform(t, [0, 1], ["0%", moveLunaY]);

  return (
    <div ref={sectionRef} className="our-services-hero container">
      <div className="our-services-hero-text">
        <motion.h1
          {...opacity}
          transition={{ delay: 0.2 }}
          className="k-title-sm md:k-title-md xl:k-title-lg"
        >
          What We Do
        </motion.h1>
        <motion.p
          {...opacity}
          transition={{ delay: 0.3 }}
          className="k-text-md md:k-text-lg lg:k-text-xl"
        >
          We turn ambitious ideas into breakthrough digital experiences—through
          strategy, design, and end-to-end production.
        </motion.p>
      </div>

      <motion.div
        className="our-services-hero-content"
        variants={appearParent}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "0px 0px -80px 0px" }}
      >
        <motion.div
          className="video-wrapper"
          style={{
            "--t": t,
            width: "min(calc((1 - var(--t)) * 760px + var(--t) * 100%), 100%)",
          }}
          variants={appearChild}
        >
          <video autoPlay loop muted playsInline className="video-el">
            <source
              src="https://s3.us-east-1.amazonaws.com/content.kollectiff.com/reels/Reel_Kollectiff_corto-16x9.webm"
              type="video/webm"
            />
            Tu navegador no soporta videos WebM.
          </video>
        </motion.div>

        <motion.div className="images-wrapper" variants={appearParent}>
          <motion.img
            src={bacardiPng}
            alt=""
            className="floating-img bacardi"
            style={{ translateX: bacardiX, translateY: bacardiY }}
            variants={appearChild}
            draggable={false}
          />
          <motion.img
            src={liquidPng}
            alt=""
            className="floating-img liquid"
            style={{ translateX: liquidX, translateY: liquidY }}
            variants={appearChild}
            draggable={false}
          />
          <motion.img
            src={lunaPng}
            alt=""
            className="floating-img luna"
            style={{ translateX: lunaX, translateY: lunaY }}
            variants={appearChild}
            draggable={false}
          />
        </motion.div>
      </motion.div>
    </div>
  );
}
